import { Controller } from "@hotwired/stimulus"

interface Particle {
  x: number
  y: number
  size: number
  speedX: number
  speedY: number
  opacity: number
  color: string
}

export default class ParticlesController extends Controller {
  static targets = ["canvas"]

  declare readonly canvasTarget: HTMLCanvasElement
  declare readonly hasCanvasTarget: boolean

  private canvas!: HTMLCanvasElement
  private ctx!: CanvasRenderingContext2D
  private particles: Particle[] = []
  private animationId: number | null = null
  private resizeHandler!: () => void

  connect() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    this.canvas = this.hasCanvasTarget
      ? this.canvasTarget
      : (document.getElementById("particles-canvas") as HTMLCanvasElement)
    if (!this.canvas) return

    const ctx = this.canvas.getContext("2d")
    if (!ctx) return
    this.ctx = ctx

    this.resize()
    this.initParticles()
    this.animate()

    this.resizeHandler = this.resize.bind(this)
    window.addEventListener("resize", this.resizeHandler)
  }

  disconnect() {
    if (this.animationId !== null) cancelAnimationFrame(this.animationId)
    window.removeEventListener("resize", this.resizeHandler)
  }

  private resize() {
    this.canvas.width = window.innerWidth
    this.canvas.height = window.innerHeight
  }

  private initParticles() {
    this.particles = []
    const count = Math.min(60, Math.floor(window.innerWidth / 20))
    for (let i = 0; i < count; i++) {
      this.particles.push(this.newParticle())
    }
  }

  private newParticle(): Particle {
    return {
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      size: Math.random() * 2 + 0.5,
      speedX: (Math.random() - 0.5) * 0.4,
      speedY: -Math.random() * 0.6 - 0.2,
      opacity: Math.random() * 0.6 + 0.1,
      color:
        Math.random() > 0.6
          ? "#D4AF37"
          : Math.random() > 0.5
            ? "#7c27ff"
            : "#6366f1",
    }
  }

  private animate() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)

    this.particles.forEach((p, i) => {
      p.x += p.speedX
      p.y += p.speedY
      p.opacity -= 0.001

      if (p.y < -10 || p.opacity <= 0) {
        this.particles[i] = { ...this.newParticle(), y: window.innerHeight + 10 }
      }

      this.ctx.save()
      this.ctx.globalAlpha = Math.max(0, p.opacity)
      this.ctx.fillStyle = p.color
      this.ctx.beginPath()
      this.ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
      this.ctx.fill()
      this.ctx.restore()
    })

    this.animationId = requestAnimationFrame(this.animate.bind(this))
  }
}
